import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  CircularProgress,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Grid,
  Typography,
  Paper,
  Chip,
  Card,
  CardContent,
  Divider,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import PropertyDetail from '@/components/PropertyDetail';
import PropertyForm from '@/components/PropertyForm';
import PropertyCard from '@/components/PropertyCard';
import ClientCard from '@/components/ClientCard';
import { Property, PropertyFormData, Client, MatchScore } from '@/types';
import { PropertyService } from '@/services/propertyService';
import { MatchingService } from '@/services/matchingService';
import { ClientService } from '@/services/clientService';
import { useAuthStore } from '@/store/authStore';
import PropertyAISalesmanWidget from '@/components/PropertyAISalesmanWidget';
import { ChantierService } from '@/services/chantierService';
import { Chantier } from '@/types';
import { Business, ChevronRight } from '@mui/icons-material';

const PropertyViewPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [property, setProperty] = useState<Property | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // New state for related content
  const [relatedProperties, setRelatedProperties] = useState<Property[]>([]);
  const [matchingClients, setMatchingClients] = useState<{ client: Client; score: MatchScore }[]>([]);
  const [loadingRelated, setLoadingRelated] = useState(false);
  const [clientMatchScore, setClientMatchScore] = useState<number | undefined>(undefined);
  const [parentChantier, setParentChantier] = useState<Chantier | null>(null);

  useEffect(() => {
    if (!id) {
      setError('Property ID is required');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    // Use real-time subscription for property updates
    const unsubscribe = PropertyService.subscribeToProperty(
      id,
      (propertyData) => {
        if (propertyData) {
          setProperty(propertyData);
          setError(null);
        } else {
          setProperty(null);
          setError('Property not found');
        }
        setLoading(false);
      },
      (err) => {
        setError(err.message || 'Failed to load property');
        setLoading(false);
      }
    );

    return () => {
      unsubscribe();
    };
  }, [id]);

  // Load related properties, matching clients, and client match score when property is loaded
  useEffect(() => {
    if (property) {
      const loadRelatedAndMatches = async () => {
        try {
          setLoadingRelated(true);
          
          // Fetch parent Chantier if exists
          if (property.chantierId) {
            const chantierData = await ChantierService.getChantierById(property.chantierId);
            setParentChantier(chantierData);
          } else {
            setParentChantier(null);
          }
          
          // Load related properties (same type, excluding current)
          // In a real app, this might be more sophisticated (e.g. same location, price range)
          const relatedResponse = await PropertyService.getProperties({
             propertyTypes: [property.metadata.propertyType],
             isActive: true
          }, 1, 5); // Fetch 5 to ensure we have enough after filtering
          
          if (relatedResponse.success && relatedResponse.data) {
             setRelatedProperties(relatedResponse.data
               .filter(p => p.id !== property.id)
               .slice(0, 3));
          }

          // Load matching clients (for agents/admins)
          if (user?.role === 'admin' || user?.role === 'agent') {
             const matches = await MatchingService.findMatchingClients(property);
             setMatchingClients(matches);
          }

          // Calculate match score for current client user
          if (user?.role === 'client' && user.profile?.email) {
            const client = await ClientService.getClientByEmail(user.profile.email);
            if (client) {
              const score = MatchingService.calculateMatchScore(client, property);
              setClientMatchScore(score.overall);
            }
          }

        } catch (err) {
          console.error("Failed to load related info", err);
        } finally {
          setLoadingRelated(false);
        }
      };
      
      loadRelatedAndMatches();
    }
  }, [property, user]);

  const handleEdit = (_property: Property) => {
    setEditDialogOpen(true);
  };

  const handleDelete = async (property: Property) => {
    if (!window.confirm(`Are you sure you want to delete "${property.metadata.title}"?`)) {
      return;
    }

    try {
      await PropertyService.deleteProperty(property.id, false); // Soft delete
      navigate('/properties');
    } catch (err: any) {
      alert(`Failed to delete property: ${err.message}`);
    }
  };

  const handleUpdateProperty = async (data: PropertyFormData, files?: File[]) => {
    if (!property) return;

    setSaving(true);
    try {
      // 1. Upload new images if any
      let newImageUrls: string[] = [];
      if (files && files.length > 0) {
         await Promise.all(
          files.map(async (file, index) => {
            try {
              const url = await PropertyService.uploadPropertyImage(property.id, file, index);
              newImageUrls.push(url);
            } catch (err) {
              console.error('Failed to upload image:', err);
            }
          })
        );
      }

      // 2. Merge new images with existing
      const updatedImages = [...(data.metadata.images || []), ...newImageUrls];
      
      const updatedData = {
        ...data,
        metadata: {
          ...data.metadata,
          images: updatedImages
        }
      };

      await PropertyService.updateProperty(property.id, updatedData);
      setEditDialogOpen(false);
      
      // Reload property data
      const updatedProperty = await PropertyService.getProperty(property.id);
      if (updatedProperty) {
        setProperty(updatedProperty);
      }
    } catch (err: any) {
      throw err;
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    navigate('/properties');
  };

  const handleViewProperty = (p: Property) => {
    navigate(`/properties/${p.id}`);
  };

  const handleViewClient = (client: Client) => {
    navigate(`/clients/${client.id}`);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !property) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error || 'Property not found'}
        </Alert>
        <Button variant="contained" onClick={handleBack}>
          Back to Properties
        </Button>
      </Box>
    );
  }

  // Check if user has permission to edit/delete
  const canEdit = user && (user.role === 'admin' || user.role === 'agent');
  const canDelete = user && (user.role === 'admin' || (user.role === 'agent' && property.agentId === user.id));

  return (
    <Box>
      <PropertyDetail
        property={property}
        matchScore={clientMatchScore}
        onEdit={canEdit ? handleEdit : undefined}
        onDelete={canDelete ? handleDelete : undefined}
        onBack={handleBack}
        showActions={!!(canEdit || canDelete)}
      />

      {/* Parent Project Section */}
      {parentChantier && (
        <Box sx={{ mt: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ mb: 2, fontWeight: 700, fontFamily: '"Playfair Display", serif' }}>
            Part of a Collection
          </Typography>
          <Card 
            variant="outlined" 
            sx={{ 
              borderRadius: 3, 
              display: 'flex', 
              overflow: 'hidden',
              cursor: 'pointer',
              transition: 'all 0.3s',
              '&:hover': { boxShadow: 3, transform: 'translateY(-2px)' }
            }}
            onClick={() => navigate(`/chantiers/${parentChantier.id}`)}
          >
            <Box 
              sx={{ 
                width: 150, 
                bgcolor: 'grey.100',
                backgroundImage: `url(${parentChantier.heroVideoUrl || parentChantier.masterPlanUrl || 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&q=80'})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center'
              }} 
            />
            <CardContent sx={{ flex: 1, p: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 800 }}>{parentChantier.name}</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary', mt: 0.5 }}>
                  <Business fontSize="small" />
                  <Typography variant="body2">Developer: {parentChantier.developerId.substring(0, 8)}...</Typography>
                </Box>
              </Box>
              <Button endIcon={<ChevronRight />} variant="text">Explore Project</Button>
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Matching Clients Section */}
      {(user?.role === 'admin' || user?.role === 'agent') && (
        <Box sx={{ mt: 4, px: 0 }}>
          <Typography variant="h5" gutterBottom sx={{ mb: 2 }}>
            Potential Client Matches
          </Typography>
          
          {loadingRelated ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : matchingClients.length > 0 ? (
            <Grid container spacing={3}>
              {matchingClients.map(({ client, score }) => (
                <Grid item xs={12} sm={6} md={4} key={client.id}>
                  <Box sx={{ position: 'relative', height: '100%' }}>
                    <ClientCard 
                      client={client} 
                      compact 
                      onView={() => handleViewClient(client)}
                    />
                    <Box 
                      sx={{ 
                        position: 'absolute', 
                        top: 10, 
                        right: 10, 
                        zIndex: 1 
                      }}
                    >
                      <Chip 
                        label={`${score.overall}% Match`} 
                        color={score.overall >= 80 ? "success" : score.overall >= 60 ? "warning" : "default"}
                        size="small"
                      />
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>
          ) : (
             <Paper sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
               No matching clients found at this time.
             </Paper>
          )}
        </Box>
      )}

      {/* Related Properties Section */}
      <Box sx={{ mt: 4, mb: 4, px: 0 }}>
        <Typography variant="h5" gutterBottom sx={{ mb: 2 }}>
          Related Properties
        </Typography>
        
        {loadingRelated ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <CircularProgress size={24} />
          </Box>
        ) : relatedProperties.length > 0 ? (
          <Grid container spacing={3}>
            {relatedProperties.map(relatedProperty => (
              <Grid item xs={12} sm={6} md={4} key={relatedProperty.id}>
                <PropertyCard 
                  property={relatedProperty} 
                  compact 
                  onView={() => handleViewProperty(relatedProperty)}
                />
              </Grid>
            ))}
          </Grid>
        ) : (
           <Paper sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
             No related properties found.
           </Paper>
        )}
      </Box>

      {/* Edit Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Edit Property
          <IconButton
            aria-label="close"
            onClick={() => setEditDialogOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <PropertyForm
            property={property}
            onSubmit={handleUpdateProperty}
            onCancel={() => setEditDialogOpen(false)}
            loading={saving}
          />
        </DialogContent>
      </Dialog>
      
      {/* Property-specific AI Salesman (acts as primary chat on this view) */}
      <PropertyAISalesmanWidget property={property} />
    </Box>
  );
};

export default PropertyViewPage;
